/**
 * 支払の記録と消込（注文書の3回目 3c、#233、docs/93 §15.5）。両製品・門 sales。登録・修正・取消は PC だけ（スマホは閲覧のみ＝PC_ONLY_FEATURES の vendorPaymentCreate）。
 *   VendorPaymentDialog：窓「支払登録」＝支払日（DateInput）・金額（既定は 残 − 先方負担の振込手数料 − 源泉）・方法（振込が既定）・振込手数料と負担（{vendor}の振込手数料の設定から）・
 *     源泉徴収（任意）・メモ。配分は既定で自動（窓を開いた仕入が先、ほかは支払期日 → 請求日の古い順＝契約の fillSettlements／splitDeductions。API の自動と同じ並べ方）、
 *     「配分修正」で仕入ごとの消込額の表。修正（paymentId）は 日・方法・メモ・配分だけ（額・手数料・源泉は取消して登録し直す）
 *   PaymentHistory：支払の履歴の表（支払日・金額・方法・振込手数料・源泉徴収・配分／この仕入・取消）。行の「⋯」で 修正・取消（確かめ）
 *   PurchasePaymentsSection：仕入の画面の節「支払」（残・「支払登録」・履歴）
 *   PartnerPaymentsSection：{vendor}の詳細の節「支払」（未払いの合計・「支払登録」＝その{vendor}の未払いを古い順に配分・今月の支払・履歴）
 * 言葉：支払・支払登録・振込手数料・先方負担／当方負担・源泉徴収・残（「〜を払う」「払った」を書かない）
 */
import {
  FEE_BEARER_LABEL,
  VENDOR_PAYMENT_LIMITS,
  VENDOR_PAYMENT_METHOD_LABEL,
  VENDOR_PAYMENT_MSG as M,
  VendorPaymentMethod,
  defaultPaymentAmount,
  displayNameOf,
  fillSettlements,
  settleOf,
  sortForPayment,
  splitDeductions,
  vendorFeeOf,
  type FeeBearer,
  type Partner,
  type PurchaseList,
  type PurchasePayments,
  type VendorPayment,
  type VendorPaymentSummary,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, messageOf } from "../../api.js";
import { DateInput } from "../../components/DateInput.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { ErrorText, Field, Help, useFieldIds, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { mdSlash, todayJst, yen } from "../../lib/num.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { DueCell } from "./parts.jsx";

/** 配分の表の1行。available＝この支払を除いた仕入の残（修正のときは、この支払が当てていた分を足し戻す） */
type Row = { purchaseId: string; purchasedOn: string; vendorInvoiceNo: string; paymentDueOn: string | null; groupName: string; balance: number; overdueDays: number };

/** 支払の動いたあとに読み直すもの（残は 仕入の一覧・仕入の画面・ホーム・{project}・{vendor}の詳細にも出る） */
export function invalidateVendorPaymentViews(qc: ReturnType<typeof useQueryClient>, vendorId: string) {
  for (const k of ["purchases", "purchase", "purchase-payments", "vendor-payment", "home", "group", "groups"]) void qc.invalidateQueries({ queryKey: [k] });
  void qc.invalidateQueries({ queryKey: ["partner", vendorId] });
}

export function VendorPaymentDialog({
  vendorId,
  vendorName,
  purchaseId,
  paymentId,
  onClose,
}: {
  vendorId: string;
  vendorName: string;
  /** 窓を開いた仕入（その仕入を先に当てる）。省くと その{vendor}の未払いを古い順 */
  purchaseId?: string | undefined;
  /** 修正するとき */
  paymentId?: string | undefined;
  onClose: (done?: VendorPayment) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const ids = useFieldIds(["paidOn", "amount", "method", "fee", "withholding", "memo"] as const);
  const today = todayJst();
  const edit = !!paymentId;
  const partner = useQuery({ queryKey: ["partner", vendorId], queryFn: () => api<Partner>("GET", `/partners/${vendorId}`), retry: false });
  const open = useQuery({
    queryKey: ["purchases", "unpaid", "", "", vendorId],
    queryFn: () => api<PurchaseList>("GET", `/purchases?tab=unpaid&vendor=${vendorId}`),
  });
  const cur = useQuery({ queryKey: ["vendor-payment", paymentId], queryFn: () => api<VendorPayment>("GET", `/vendor-payments/${paymentId}`), enabled: edit });

  const [paidOn, setPaidOn] = useState(today);
  const [amount, setAmount] = useState<number | null>(null);
  const [amountTouched, setAmountTouched] = useState(false);
  const [method, setMethod] = useState<VendorPaymentMethod>("bank_transfer");
  const [fee, setFee] = useState<number>(0);
  const [bearer, setBearer] = useState<FeeBearer | null>(null);
  const [withholding, setWithholding] = useState<number>(0);
  const [memo, setMemo] = useState("");
  const [showAlloc, setShowAlloc] = useState(false);
  const [manual, setManual] = useState<Map<string, number> | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const loaded = useRef(false);
  // 二度押し・通信のやり直しで支払が2件にならない（API は同じキーの2回目に1回目の答えを返す）
  const idemKey = useRef(`v${crypto.randomUUID().replace(/-/g, "")}`);

  // 修正：今の値を入れる（1回だけ）
  useEffect(() => {
    if (!edit || !cur.data || loaded.current) return;
    loaded.current = true;
    const v = cur.data;
    setPaidOn(v.paidOn);
    setAmount(v.amount);
    setMethod(v.method);
    setFee(v.feeAmount);
    setBearer(v.feeBearer);
    setWithholding(v.withholdingTaxAmount);
    setMemo(v.memo);
    setManual(new Map(v.allocations.map((a) => [a.purchaseId, a.amount + a.fee + a.withholding])));
  }, [edit, cur.data]);
  // 振込手数料の負担の既定は{vendor}の設定（振込手数料 当方＝当方負担。ほかは先方負担）
  const feeBearer: FeeBearer = bearer ?? (partner.data?.transferFeeBearer === "us" ? "own" : "vendor");

  // 表の行：残のある仕入（修正のときは、この支払が当てている仕入の分を足し戻す）。窓を開いた仕入が先、ほかは古い順
  const rows: Row[] | null = useMemo(() => {
    if (!open.data || (edit && !cur.data)) return null;
    const mine = new Map((cur.data?.allocations ?? []).map((a) => [a.purchaseId, a]));
    const base: Row[] = open.data.items
      .filter((x) => x.status === "confirmed")
      .map((x) => {
        const m = mine.get(x.id);
        return { purchaseId: x.id, purchasedOn: x.purchasedOn, vendorInvoiceNo: x.vendorInvoiceNo, paymentDueOn: x.paymentDueOn, groupName: x.group?.name ?? "", balance: x.balance + (m ? m.amount + m.fee + m.withholding : 0), overdueDays: x.overdueDays };
      });
    for (const a of cur.data?.allocations ?? []) {
      if (base.some((r) => r.purchaseId === a.purchaseId)) continue;
      base.push({ purchaseId: a.purchaseId, purchasedOn: a.purchasedOn, vendorInvoiceNo: a.vendorInvoiceNo, paymentDueOn: a.paymentDueOn, groupName: a.groupName, balance: a.balance + a.amount + a.fee + a.withholding, overdueDays: 0 });
    }
    const sorted = sortForPayment(base);
    const first = purchaseId ? sorted.filter((r) => r.purchaseId === purchaseId) : [];
    return [...first, ...sorted.filter((r) => r.purchaseId !== purchaseId)];
  }, [open.data, cur.data, edit, purchaseId]);

  const target = purchaseId ? rows?.find((r) => r.purchaseId === purchaseId) : undefined;
  const baseBalance = target ? target.balance : (rows ?? []).reduce((s, r) => s + r.balance, 0);
  // 金額の既定（打つまで）：残 − 先方負担の振込手数料 − 源泉
  const amountValue = edit || amountTouched ? amount : rows ? defaultPaymentAmount(baseBalance, fee, feeBearer, withholding) : null;
  const vendorFee = vendorFeeOf(fee, feeBearer);
  const settleTotal = settleOf(amountValue ?? 0, fee, feeBearer, withholding);
  const settles = useMemo(() => {
    if (!rows) return [];
    if (manual) return rows.map((r) => ({ purchaseId: r.purchaseId, settle: Math.max(0, manual.get(r.purchaseId) ?? 0) }));
    return fillSettlements(rows, settleTotal);
  }, [rows, manual, settleTotal]);
  const settled = settles.reduce((s, x) => s + x.settle, 0);
  const over = settled > settleTotal;
  const overRow = rows?.find((r) => (settles.find((s) => s.purchaseId === r.purchaseId)?.settle ?? 0) > r.balance);
  // 登録後の残：窓を開いた仕入があればその仕入の残、無ければ その{vendor}の未払いの合計
  const rest = target ? target.balance - (settles.find((x) => x.purchaseId === target.purchaseId)?.settle ?? 0) : (rows ?? []).reduce((s, r) => s + r.balance, 0) - settled;
  const unallocated = Math.max(0, settleTotal - settled);

  const setSettle = (id: string, v: number) => {
    const m = new Map(manual ?? settles.map((s) => [s.purchaseId, s.settle] as const));
    m.set(id, Math.max(0, v));
    setManual(m);
  };

  const submit = async () => {
    setError(undefined);
    if (!amountValue || amountValue < 1) return setError(M.amountInvalid);
    if (paidOn > today) return setError(M.futureDate);
    if (over) return setError(M.overAllocated);
    if (overRow) return setError(M.overPurchase);
    const allocations = splitDeductions(settles, vendorFee, withholding);
    setBusy(true);
    try {
      let v: VendorPayment;
      if (!edit) {
        v = await api<VendorPayment>(
          "POST",
          "/vendor-payments",
          { vendorId, paidOn, amount: amountValue, method, feeAmount: fee, feeBearer, withholdingTaxAmount: withholding, memo: memo.trim() || undefined, allocations },
          { "idempotency-key": idemKey.current },
        );
      } else {
        const c = cur.data!;
        v = c;
        if (paidOn !== c.paidOn || method !== c.method || memo !== c.memo) {
          v = await api<VendorPayment>("PUT", `/vendor-payments/${c.id}`, { version: v.updatedAt, paidOn, method, memo: memo.trim() ? memo : null });
        }
        const before = JSON.stringify(c.allocations.map((a) => [a.purchaseId, a.amount, a.fee, a.withholding]).sort());
        const after = JSON.stringify(allocations.map((a) => [a.purchaseId, a.amount, a.fee, a.withholding]).sort());
        if (before !== after) v = await api<VendorPayment>("PUT", `/vendor-payments/${c.id}/allocations`, { version: v.updatedAt, allocations });
      }
      invalidateVendorPaymentViews(qc, vendorId);
      toast(edit ? M.updated : M.created);
      onClose(v);
    } catch (e) {
      if (e instanceof ApiError && e.code === "VENDOR_PAYMENT_STALE") void cur.refetch();
      setError(messageOf(e));
      if (e instanceof ApiError && e.status >= 500) idemKey.current = `v${crypto.randomUUID().replace(/-/g, "")}`;
    } finally {
      setBusy(false);
    }
  };

  const ro = (v: string) => <div className="inp ro num">{v}</div>;
  const waiting = !rows || (edit && !cur.data);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg wide vpd" role="dialog" aria-modal="true" aria-label={edit ? M.editTitle : M.dialogTitle} onKeyDown={(e) => e.key === "Escape" && onClose()} data-testid="vendor-payment-dialog">
        <div className="dh">
          {edit ? M.editTitle : M.dialogTitle}
          <span className="vpd-vendor" title={vendorName}>{vendorName}</span>
        </div>
        <div className="db">
          {open.isError ? <ErrorText>{messageOf(open.error)}</ErrorText> : null}
          {cur.isError ? <ErrorText>{messageOf(cur.error)}</ErrorText> : null}
          <div className="vpd-grid">
            <Field id={ids.paidOn} label={M.paidOn}>
              <DateInput id={ids.paidOn} label={M.paidOn} max={today} clearable={false} value={paidOn} onChange={(v) => v && setPaidOn(v)} testId="vendor-payment-date" />
            </Field>
            <Field id={ids.method} label={M.method}>
              <select id={ids.method} className="inp" value={method} onChange={(e) => setMethod(VendorPaymentMethod.parse(e.target.value))} data-testid="vendor-payment-method">
                {VendorPaymentMethod.options.map((m) => (
                  <option key={m} value={m}>{VENDOR_PAYMENT_METHOD_LABEL[m]}</option>
                ))}
              </select>
            </Field>
            <Field id={ids.amount} label={`${M.amount}（円）`} help={<Help label={`${M.amount}の説明`} text={M.helpAmount} testId="help-vendor-payment-amount" />}>
              {edit ? ro(yen(amountValue ?? 0)) : (
                <NumInput id={ids.amount} kind="int" className="inp num" placeholder=" " value={amountValue} onChange={(v) => { setAmountTouched(true); setAmount(v); }} disabled={waiting} data-testid="vendor-payment-amount" />
              )}
            </Field>
            <Field id={ids.withholding} label={`${M.withholding}（円）`} help={<Help label={`${M.withholding}の説明`} text={M.helpWithholding} testId="help-vendor-payment-withholding" />}>
              {edit ? ro(yen(withholding)) : (
                <NumInput id={ids.withholding} kind="int" className="inp num" placeholder=" " showZero value={withholding} onChange={(v) => setWithholding(Math.max(0, v ?? 0))} data-testid="vendor-payment-withholding" />
              )}
            </Field>
            <Field id={ids.fee} label={`${M.fee}（円）`}>
              {edit ? ro(yen(fee)) : (
                <NumInput id={ids.fee} kind="int" className="inp num" placeholder=" " showZero value={fee} onChange={(v) => setFee(Math.max(0, v ?? 0))} data-testid="vendor-payment-fee" />
              )}
            </Field>
            <div className="vpd-bearer">
              <span className="vpd-lbl" id="vpd-bearer-l">
                {M.feeBearer}
                <Help label={`${M.feeBearer}の説明`} text={M.helpFee} testId="help-vendor-payment-fee" />
              </span>
              <Segmented
                options={(["vendor", "own"] as const).map((b) => ({ value: b, label: FEE_BEARER_LABEL[b], testId: `vendor-payment-bearer-${b}` }))}
                value={feeBearer}
                onChange={(v) => setBearer(v)}
                labelledBy="vpd-bearer-l"
                disabled={edit}
              />
            </div>
            <Field id={ids.memo} label={M.memo} className="vpd-memo">
              <input id={ids.memo} className="inp" value={memo} maxLength={VENDOR_PAYMENT_LIMITS.memo} onChange={(e) => setMemo(e.target.value)} data-testid="vendor-payment-memo" />
            </Field>
          </div>
          {edit ? <p className="hint vpd-fixed">{t(M.amountFixed)}</p> : null}

          {/* 消込額と登録後の残。配分は既定で自動・「配分修正」で仕入ごとの消込額 */}
          <div className="vpd-sum" data-testid="vendor-payment-sum">
            <span>
              {M.settled} <b className="num" data-testid="vendor-payment-settled">{waiting ? "…" : yen(settled)}</b> 円
            </span>
            <span>{waiting ? "" : M.restAfter(yen(Math.max(0, rest)))}</span>
            <span className="sp" />
            <button type="button" className="btn text" aria-expanded={showAlloc} onClick={() => setShowAlloc(!showAlloc)} disabled={waiting} data-testid="vendor-payment-allocate">
              {M.allocate} {showAlloc ? "⌃" : "⌄"}
            </button>
            <Help label={`${M.allocate}の説明`} text={M.helpAllocate} testId="help-vendor-payment-allocate" />
          </div>
          {!waiting && unallocated > 0 && !over ? <p className="hint vpd-note" data-testid="vendor-payment-unallocated">{M.restNote(yen(unallocated))}</p> : null}
          {showAlloc && rows ? (
            rows.length === 0 ? (
              <p className="hint" data-testid="vendor-payment-no-open">{M.noOpen}</p>
            ) : (
              <div className="tbl-wrap vpd-tbl">
                <table className="list" data-testid="vendor-payment-alloc">
                  <thead>
                    <tr>
                      <th>{M.colBillDate}</th>
                      <th>{M.colInvoiceNo}</th>
                      <th>{M.colDue}</th>
                      <th className="amt">{M.colBalance}</th>
                      <th className="amt">{M.colSettle}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const s = settles.find((x) => x.purchaseId === r.purchaseId)?.settle ?? 0;
                      return (
                        <tr key={r.purchaseId} data-testid="vendor-payment-alloc-row" data-purchase={r.purchaseId}>
                          <td className="num">{mdSlash(r.purchasedOn)}</td>
                          <td className="num">{r.vendorInvoiceNo || <span className="muted">—</span>}</td>
                          <td><DueCell dueOn={r.paymentDueOn} overdueDays={r.overdueDays} /></td>
                          <td className="amt num">{yen(r.balance)}</td>
                          <td className="amt">
                            <NumInput kind="int" showZero className="inp num" value={s} onChange={(v) => setSettle(r.purchaseId, v ?? 0)} aria-label={`${r.vendorInvoiceNo || mdSlash(r.purchasedOn)} の${M.colSettle}`} style={{ width: 132 }} data-testid="vendor-payment-alloc-amount" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {manual ? (
                  <button type="button" className="btn text" onClick={() => setManual(null)} data-testid="vendor-payment-auto">{M.allocateAuto}</button>
                ) : null}
              </div>
            )
          ) : null}
          {over ? <ErrorText>{M.overAllocated}</ErrorText> : overRow ? <ErrorText>{M.overPurchase}</ErrorText> : null}
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={() => onClose()}>取消</button>
          <button type="button" className="btn primary" disabled={busy || waiting} onClick={() => void submit()} data-testid="vendor-payment-submit">
            {edit ? M.saveEdit : M.save}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 支払の履歴の表。forPurchase＝仕入の画面（「この仕入」の列）。行の「⋯」で 修正・取消（PC だけ） */
export function PaymentHistory({ items, forPurchase, testId }: { items: VendorPaymentSummary[]; forPurchase?: boolean; testId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const pc = usePcOnly();
  const [editing, setEditing] = useState<VendorPaymentSummary | null>(null);
  const [voiding, setVoiding] = useState<VendorPaymentSummary | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!items.length) return <p className="hint" data-testid={`${testId}-empty`}>{M.historyEmpty}</p>;
  const doVoid = async () => {
    if (!voiding) return;
    setBusy(true);
    try {
      await api<VendorPayment>("POST", `/vendor-payments/${voiding.id}/void`, { version: voiding.updatedAt, reason });
      invalidateVendorPaymentViews(qc, voiding.vendorId);
      toast(M.voided);
      setVoiding(null);
      setReason("");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="tbl-wrap">
        <table className="list vph" data-testid={testId}>
          <thead>
            <tr>
              <th>{M.colPaidOn}</th>
              <th className="amt">{M.colAmount}</th>
              <th>{M.colMethod}</th>
              <th className="amt">{M.colFee}</th>
              <th className="amt">{M.colWithholding}</th>
              <th className="amt">{forPurchase ? M.colThis : M.colTarget}</th>
              <th aria-label="操作" />
            </tr>
          </thead>
          <tbody>
            {items.map((x) => {
              const menu: RowMenuItem[] = [
                { label: M.edit, testId: "vendor-payment-edit", onSelect: () => setEditing(x) },
                { label: M.void, danger: true, divider: true, testId: "vendor-payment-void", onSelect: () => setVoiding(x) },
              ];
              const share = x.forPurchase ? x.forPurchase.amount + x.forPurchase.fee + x.forPurchase.withholding : x.settled;
              return (
                <tr key={x.id} className={x.voided ? "is-voided" : undefined} data-testid="vendor-payment-row" data-voided={x.voided ? "1" : undefined}>
                  <td className="num">
                    {mdSlash(x.paidOn)}
                    {x.voided ? <span className="chip quiet vph-void" data-testid="vendor-payment-voided">{M.voidMark}</span> : null}
                  </td>
                  <td className="amt num"><b>{yen(x.amount)}</b></td>
                  <td>{VENDOR_PAYMENT_METHOD_LABEL[x.method]}</td>
                  <td className="amt num">
                    {x.feeAmount ? <>{yen(x.feeAmount)}<div className="vph-sub">{FEE_BEARER_LABEL[x.feeBearer]}</div></> : <span className="muted">—</span>}
                  </td>
                  <td className="amt num">{x.withholdingTaxAmount ? yen(x.withholdingTaxAmount) : <span className="muted">—</span>}</td>
                  <td className="amt num">
                    {yen(share)}
                    {!forPurchase && x.purchaseCount ? (
                      <div className="vph-sub">{x.firstInvoiceNo ? `${x.firstInvoiceNo}${x.purchaseCount > 1 ? ` ほか ${x.purchaseCount - 1}件` : ""}` : `${x.purchaseCount}件`}</div>
                    ) : null}
                  </td>
                  <td className="vph-act">{x.voided || pc.phone ? null : <RowMenu items={menu} label={M.menu(mdSlash(x.paidOn))} testId="vendor-payment-menu" />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing ? <VendorPaymentDialog vendorId={editing.vendorId} vendorName={editing.vendorName} paymentId={editing.id} onClose={() => setEditing(null)} /> : null}
      {voiding ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setVoiding(null)}>
          <div className="dlg" role="alertdialog" aria-label={M.voidTitle} onKeyDown={(e) => e.key === "Escape" && setVoiding(null)}>
            <div className="dh">{M.voidTitle}</div>
            <div className="db">
              <p style={{ marginTop: 0 }}>{M.voidConfirm}</p>
              <p style={{ margin: 0 }} className="num">
                {mdSlash(voiding.paidOn)}　{voiding.vendorName}　{yen(voiding.amount)} 円
              </p>
              <Field id="vpv_reason" label={M.voidReason}>
                <input id="vpv_reason" className="inp" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-testid="vendor-payment-void-reason" />
              </Field>
            </div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setVoiding(null)}>閉じる</button>
              <button type="button" className="btn shu" disabled={busy} onClick={() => void doVoid()} data-testid="vendor-payment-void-confirm">{M.void}</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** 仕入の画面の節「支払」（確定・取消の仕入）。残・「支払登録」（PC・残があるとき）・履歴 */
export function PurchasePaymentsSection({ purchaseId, vendorId, vendorName, confirmed }: { purchaseId: string; vendorId: string; vendorName: string; confirmed: boolean }) {
  const pc = usePcOnly();
  const [dlg, setDlg] = useState(false);
  const q = useQuery({ queryKey: ["purchase-payments", purchaseId], queryFn: () => api<PurchasePayments>("GET", `/purchases/${purchaseId}/payments`) });
  const d = q.data;
  return (
    <section className="rqe-band pce-pay" aria-label={M.section} data-testid="purchase-payments">
      <div className="pce-pay-head">
        <h2 className="pce-pay-t">
          {M.section}
          <Help label={`${M.history}の説明`} text={M.helpHistory} testId="help-purchase-payments" />
        </h2>
        <span className="pce-kv">
          <span>{M.balance}</span>
          <b className="num" data-testid="purchase-payments-balance">{d ? yen(d.balance) : "…"}</b>
          <span className="u">円</span>
        </span>
        {d && d.paid ? (
          <span className="pce-kv">
            <span>{M.settled}</span>
            <b className="num" data-testid="purchase-payments-paid">{yen(d.paid)}</b>
            <span className="u">円</span>
          </span>
        ) : null}
        <span className="sp" />
        {confirmed && d && d.balance > 0 && !pc.phone ? (
          <button type="button" className="btn primary" onClick={() => setDlg(true)} data-testid="purchase-pay">{M.create}</button>
        ) : null}
      </div>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {d ? <PaymentHistory items={d.items} forPurchase testId="purchase-payment-history" /> : <div className="pce-pay-wait" aria-busy="true" />}
      {dlg ? <VendorPaymentDialog vendorId={vendorId} vendorName={vendorName} purchaseId={purchaseId} onClose={() => setDlg(false)} /> : null}
    </section>
  );
}

/** {vendor}の詳細の節「支払」（未払いの合計・「支払登録」＝その{vendor}の未払いを古い順に配分・今月の支払・履歴） */
export function PartnerPaymentsSection({ partner }: { partner: Partner }) {
  const pc = usePcOnly();
  const [dlg, setDlg] = useState(false);
  const p = partner.payable;
  const h = partner.vendorPayments;
  return (
    <div data-testid="partner-payments">
      <div className="pdpay-sum">
        <span className="pce-kv">
          <span>{M.payableTotal}</span>
          <b className="num" data-testid="partner-payable-count">{p?.count ?? 0}</b>
          <span className="u">件</span>
          <b className="num" data-testid="partner-payable-total">{yen(p?.total ?? 0)}</b>
          <span className="u">円</span>
        </span>
        {p?.overdueTotal ? (
          <span className="shu pdpay-over">
            うち期限超過 <span className="num">{yen(p.overdueTotal)}</span> 円
          </span>
        ) : null}
        {h ? (
          <span className="pce-kv">
            <span>{M.thisMonth}</span>
            <b className="num" data-testid="partner-paid-month">{yen(h.thisMonth.total)}</b>
            <span className="u">円</span>
          </span>
        ) : null}
        <span className="sp" />
        {p && p.count > 0 && !pc.phone && !partner.archivedAt ? (
          <button type="button" className="btn primary" onClick={() => setDlg(true)} data-testid="partner-pay">{M.createAll}</button>
        ) : null}
      </div>
      <h3 className="pdpay-h">{M.history}</h3>
      <PaymentHistory items={h?.items ?? []} testId="partner-payment-history" />
      {dlg ? <VendorPaymentDialog vendorId={partner.id} vendorName={displayNameOf(partner)} onClose={() => setDlg(false)} /> : null}
    </div>
  );
}
